/* eslint-disable consistent-return */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Align, Chart as ChartJS, TitleOptions } from 'chart.js';

import { FormatProperties } from 'models/response/modelOutput.interface';

import formatValue from './FormatValues';

ChartJS.register(ChartDataLabels);

export interface PositionLegend {
  position: TitleOptions['position'];
  align: Align;
}

interface Properties {
  chartFormatting?: any;
  chartType?: string;
  isLegend?: boolean;
  pageWidth?: number;
  formatProperties?: FormatProperties;
  pageName?: string;
  aspectRatio: number;
  pageHeight?: number;
  legendPosition?: PositionLegend;
  hasPercentageDataType?: boolean;
}

const UNIT_MULTIPLIERS: any = {
  k: 1000,
  mn: 1_000_000,
  bn: 1_000_000_000,
  tn: 1_000_000_000_000,
  auto: 1,
};

const formatYAxisValue = (data: string): string => {
  if (!data || Number.isNaN(Number(data))) {
    return data;
  }

  // Parse the value as a number
  const parsedValue = Number.parseInt(data, 10);
  const isNegative = parsedValue < 0;

  // Use the absolute value for formatting
  const value = Math.abs(parsedValue);

  if (value >= UNIT_MULTIPLIERS.tn) {
    return `${isNegative ? '-' : ''}${(value / UNIT_MULTIPLIERS.tn).toFixed(1)} T`;
  }
  if (value >= UNIT_MULTIPLIERS.bn) {
    return `${isNegative ? '-' : ''}${(value / UNIT_MULTIPLIERS.bn).toFixed(1)} B`;
  }
  if (value >= UNIT_MULTIPLIERS.mn) {
    return `${isNegative ? '-' : ''}${(value / UNIT_MULTIPLIERS.mn).toFixed(1)} M`;
  }
  if (value >= UNIT_MULTIPLIERS.k) {
    return `${isNegative ? '-' : ''}${(value / UNIT_MULTIPLIERS.k).toFixed(1)} K`;
  }

  // Handle small or negative numbers
  const formattedValue = value.toFixed(1);
  const returnValue = isNegative ? `-${formattedValue}` : formattedValue;
  return returnValue;
};

const getDashboradChartOptions = ({
  chartFormatting,
  isLegend = false,
  pageWidth = 0,
  pageHeight = 0,
  formatProperties,
  pageName = '',
  aspectRatio,
  legendPosition = { position: 'top' as TitleOptions['position'], align: 'end' as Align },
  hasPercentageDataType = false,
}: Properties) => {
  const formatDataLabel = (value: any) => {
    // let total: any = 0;
    // context.chart.data.datasets.forEach((dataset: any) => {
    //   total += dataset.data[context.dataIndex];
    // });
    if (chartFormatting?.units === 'auto') {
      return `${value
        .toFixed(chartFormatting?.decimalPlaces)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }

    return `${(value / UNIT_MULTIPLIERS[chartFormatting?.units])
      .toFixed(chartFormatting?.decimalPlaces)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${chartFormatting?.units}`;
  };

  return {
    aspectRatio,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      title: { display: false },
      tooltip: {
        color: '#979797',
        callbacks: {
          label(context: any) {
            const label = context.dataset.label || '';
            const value = context.raw || context.formattedValue;

            if (formatProperties?.measure === '%') {
              return `${label}: ${(value * 100).toFixed(2)}%`;
            }

            if (chartFormatting?.units === 'auto') {
              return `${label}: ${value
                .toFixed(chartFormatting?.decimalPlaces || 2)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
            }

            return `${label}: ${(value / UNIT_MULTIPLIERS[chartFormatting?.units])
              .toFixed(chartFormatting?.decimalPlaces || 2)
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${chartFormatting?.units}`;
          },
        },
      },
      legend: {
        display: isLegend,
        position: legendPosition.position,
        align: legendPosition.align,
        labels: {
          boxWidth: pageWidth < 576 ? 10 : 15,
          boxHeight: pageHeight < 576 ? 10 : 15,
          color: '#636363',
        },
      },
      datalabels: {
        display: !!chartFormatting?.displayValue,
        color: 'black',
        font: {
          size: 9,
        },
        align: 'end',
        anchor: 'end',
        position: 'top',
        formatter: formatDataLabel,
      },
    },
    scales: {
      x: {
        stacked: chartFormatting?.stackBar,
        grid: {
          color: 'transparent',
          borderColor: '#97979766',
        },
        ticks: {
          autoSkip: true,
          maxRotation: 0,
          minRotation: 0,
          color: '#212121',
          padding: 10,
        },
      },
      y: {
        beginAtZero: true,
        stacked: chartFormatting?.stackBar,
        grid: {
          color: '#97979766',
          borderColor: 'transparent',
          tickColor: 'transparent',
        },
        ticks: {
          color: '#212121',
          padding: 0,
          callback(value: any) {
            if (pageName === 'drivers') {
              if (formatProperties?.measure === '%') {
                return `${value * 100}%`;
              }
              return `${formatValue(value, formatProperties as FormatProperties)}`;
            }
            // If any indicator has data_type === "percentage", format with 2 decimal places
            if (hasPercentageDataType) {
              return Number(value).toFixed(2);
            }
            return formatYAxisValue(value);
          },
        },
      },
    },
  };
};

export default getDashboradChartOptions;
